import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { useCompany } from '@/context/CompanyContext';
import usePermissions from '@/hooks/usePermissions';
import PullToRefresh from '@/components/PullToRefresh';
import AdminAlertBanner from '@/components/AdminAlertBanner';
import AtRiskCard from '@/components/AtRiskCard';
import {
  Users,
  TrendingUp,
  TrendingDown,
  Truck,
  Package,
  DollarSign,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Clock,
  Calendar,
  Timer,
  Award,
  CheckCircle,
  XCircle,
  Bell,
  Megaphone,
  Wallet,
  Target,
  ShoppingCart,
  Filter,
  ChevronLeft,
  ChevronRight,
  Trophy,
  Store,
  Zap,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarPicker } from '@/components/ui/calendar';
import { toast } from 'sonner';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Small trend chip used next to KPI values when compare-to-previous-period
// is enabled. Green ↗ for positive, red ↘ for negative, muted for no change.
// ``invert`` flips the green/red logic so that a DROP in failed orders
// reads as positive (green) instead of negative.
const DeltaChip = ({ value, invert = false, testid }) => {
  if (value === null || value === undefined) return null;
  const isPositive = invert ? value < 0 : value > 0;
  const isNegative = invert ? value > 0 : value < 0;
  const color = isPositive
    ? 'text-green-400 bg-green-500/10'
    : isNegative
    ? 'text-red-400 bg-red-500/10'
    : 'text-wt-text-muted bg-wt-subtle';
  const arrow = value > 0 ? '↗' : value < 0 ? '↘' : '→';
  const label = `${arrow} ${value > 0 ? '+' : ''}${value}%`;
  return (
    <span
      className={`inline-flex items-center text-[10px] font-medium px-1.5 py-0.5 rounded ${color}`}
      data-testid={testid}
    >
      {label}
    </span>
  );
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { formatCurrency } = useSettings();
  const { getEffectiveCompanyId } = useCompany();
  const { hasPermission, isAdmin, loading: permissionsLoading } = usePermissions();
  const [stats, setStats] = useState(null);
  const [driverStats, setDriverStats] = useState(null);
  const [driverPerformance, setDriverPerformance] = useState(null);
  const [upcomingReminders, setUpcomingReminders] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [driverCash, setDriverCash] = useState(null);
  const [incentives, setIncentives] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('30');
  // Custom date range (ISO strings). Populated when dateFilter === 'custom'.
  const [customRange, setCustomRange] = useState({ from: null, to: null });
  const [customCalendarOpen, setCustomCalendarOpen] = useState(false);
  // "Compare to previous period" toggle — when on, backend returns a
  // `deltas` dict we use to render ↗ +X% / ↘ -X% chips per KPI.
  const [compareMode, setCompareMode] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Company modules state
  const [modulesEnabled, setModulesEnabled] = useState({
    hr: true,
    inventory: true,
    orders: true,
    drivers: true,
    inbox: true,
    customers: true,
    analytics: true,
    integrations: true,
    journeys: true
  });
  
  // User-specific dashboard states
  const [userStats, setUserStats] = useState(null);
  const [myRank, setMyRank] = useState(null);
  const [availableRewards, setAvailableRewards] = useState([]);

  // Permission-based feature access
  const canViewAnalytics = hasPermission('dashboard.analytics');
  const canViewLeads = hasPermission('leads.view');
  const canViewDeliveries = hasPermission('deliveries.view');
  const canCollectCash = hasPermission('deliveries.collect_cash');
  const canViewAssignedOrders = hasPermission('orders.view_assigned');
  const canViewOrders = hasPermission('orders.view');
  const canViewPickups = hasPermission('vendor_pickups.view');
  const canViewPOs = hasPermission('purchase_orders.view');

  // Load modules on mount
  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/company/modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModulesEnabled(response.data.modules_enabled || {});
    } catch (error) {
      console.error('Failed to load modules');
    }
  };

  useEffect(() => {
    // Wait for permissions to load before fetching data
    // Also reload when company context changes (for Super Admin switching)
    if (!permissionsLoading) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateFilter, customRange.from, customRange.to, compareMode, selectedMonth, selectedYear, permissionsLoading, getEffectiveCompanyId]);

  const loadAllData = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([
        isAdmin ? loadStats() : Promise.resolve(),
        loadAnnouncements(),
        (canViewAnalytics && modulesEnabled.drivers) ? loadDriverPerformance() : Promise.resolve(),
        canViewLeads ? loadUpcomingReminders() : Promise.resolve(),
        (canViewDeliveries && modulesEnabled.drivers) ? loadDriverStats() : Promise.resolve(),
        canCollectCash ? loadDriverCash() : Promise.resolve(),
        loadIncentives(),
        // User-specific data
        !isAdmin ? loadUserStats() : Promise.resolve(),
        !isAdmin ? loadMyRank() : Promise.resolve(),
        loadAvailableRewards()
      ]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, canViewAnalytics, canViewLeads, canViewDeliveries, canCollectCash, modulesEnabled, getEffectiveCompanyId, dateFilter, customRange.from, customRange.to, compareMode, selectedMonth, selectedYear]);

  // Pull to refresh handler
  const handlePullRefresh = useCallback(async () => {
    await loadAllData();
    toast.success('Dashboard refreshed');
  }, [loadAllData]);

  const loadUserStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dashboard/user-stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserStats(response.data);
    } catch (error) {
      console.error('Failed to load user stats');
    }
  };

  const loadMyRank = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/dashboard/my-rank`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyRank(response.data);
    } catch (error) {
      console.error('Failed to load rank');
    }
  };

  const loadAvailableRewards = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/incentive-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableRewards(response.data.rules || []);
    } catch (error) {
      console.error('Failed to load rewards');
    }
  };

  // Build the query-string fragment driving ALL date-filtered dashboard
  // requests. Presets map to a simple `days=N` param; Today / Yesterday /
  // Custom convert to explicit `start_date` + `end_date` ISO timestamps so
  // the backend doesn't have to guess the timezone window.
  const getDateParams = useCallback(() => {
    const now = new Date();
    const iso = (d) => d.toISOString();
    if (dateFilter === 'today') {
      const start = new Date(now); start.setHours(0, 0, 0, 0);
      return `start_date=${encodeURIComponent(iso(start))}&end_date=${encodeURIComponent(iso(now))}`;
    }
    if (dateFilter === 'yesterday') {
      const start = new Date(now); start.setHours(0, 0, 0, 0); start.setDate(start.getDate() - 1);
      const end = new Date(start); end.setHours(23, 59, 59, 999);
      return `start_date=${encodeURIComponent(iso(start))}&end_date=${encodeURIComponent(iso(end))}`;
    }
    if (dateFilter === 'custom' && customRange.from && customRange.to) {
      const start = new Date(customRange.from); start.setHours(0, 0, 0, 0);
      const end = new Date(customRange.to); end.setHours(23, 59, 59, 999);
      return `start_date=${encodeURIComponent(iso(start))}&end_date=${encodeURIComponent(iso(end))}`;
    }
    // Preset "Last N days"
    return `days=${dateFilter}`;
  }, [dateFilter, customRange.from, customRange.to]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const effectiveCompanyId = getEffectiveCompanyId();
      const companyParam = effectiveCompanyId ? `&company_id=${effectiveCompanyId}` : '';
      const compareParam = compareMode ? '&compare=true' : '';
      const response = await axios.get(`${API_BASE_URL}/dashboard/stats?${getDateParams()}${companyParam}${compareParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats');
    }
  };

  const loadDriverStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const effectiveCompanyId = getEffectiveCompanyId();
      const companyParam = effectiveCompanyId ? `?company_id=${effectiveCompanyId}` : '';
      const response = await axios.get(`${API_BASE_URL}/dashboard/driver-stats${companyParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriverStats(response.data);
    } catch (error) {
      console.error('Failed to load driver stats');
    }
  };

  const loadDriverPerformance = async () => {
    try {
      const token = localStorage.getItem('token');
      const effectiveCompanyId = getEffectiveCompanyId();
      const companyParam = effectiveCompanyId ? `&company_id=${effectiveCompanyId}` : '';
      const response = await axios.get(
        `${API_BASE_URL}/dashboard/driver-performance?month=${selectedMonth}&year=${selectedYear}${companyParam}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDriverPerformance(response.data);
    } catch (error) {
      console.error('Failed to load driver performance');
    }
  };

  const loadAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      const effectiveCompanyId = getEffectiveCompanyId();
      const companyParam = effectiveCompanyId ? `?company_id=${effectiveCompanyId}` : '';
      const response = await axios.get(`${API_BASE_URL}/announcements${companyParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data.announcements?.slice(0, 3) || []);
    } catch (error) {
      console.error('Failed to load announcements');
    }
  };

  const loadDriverCash = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE_URL}/cash/driver-pending`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDriverCash(response.data);
    } catch (error) {
      console.error('Failed to load driver cash');
    }
  };

  const loadIncentives = async () => {
    try {
      const token = localStorage.getItem('token');
      // Always request own incentives for dashboard (personal stats)
      const response = await axios.get(`${API_BASE_URL}/incentives?user_id=${user?.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIncentives(response.data);
    } catch (error) {
      console.error('Failed to load incentives');
    }
  };

  const loadUpcomingReminders = async () => {
    try {
      const token = localStorage.getItem('token');
      const effectiveCompanyId = getEffectiveCompanyId();
      const companyParam = effectiveCompanyId ? `?company_id=${effectiveCompanyId}` : '';
      const response = await axios.get(`${API_BASE_URL}/reminders/upcoming${companyParam}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUpcomingReminders(response.data.reminders || []);
    } catch (error) {
      console.error('Failed to load reminders');
    }
  };

  const changeMonth = (direction) => {
    if (direction === 'prev') {
      if (selectedMonth === 1) {
        setSelectedMonth(12);
        setSelectedYear(selectedYear - 1);
      } else {
        setSelectedMonth(selectedMonth - 1);
      }
    } else {
      if (selectedMonth === 12) {
        setSelectedMonth(1);
        setSelectedYear(selectedYear + 1);
      } else {
        setSelectedMonth(selectedMonth + 1);
      }
    }
  };

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-wt-gold"></div>
      </div>
    );
  }

  return (
    <PullToRefresh onRefresh={handlePullRefresh} disabled={loading}>
    <div className="p-4 md:p-6 space-y-6 pb-20">
      {/* Admin Alert Banner — silent integration-health warnings.
          Renders nothing for non-admins and when there are no active
          alerts, so this is zero visual cost on the happy path. */}
      <AdminAlertBanner />

      {/* Delivery/production promises at risk — admins/analytics only */}
      {canViewAnalytics && <AtRiskCard />}

      {/* Header with Date Filter */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-playfair text-wt-text-primary">
            {canViewDeliveries && !canViewAnalytics ? 'Driver Dashboard' : 'Dashboard'}
          </h1>
          <p className="text-wt-text-muted text-sm mt-1">
            Welcome back, {user?.full_name}
          </p>
        </div>
        
        {canViewAnalytics && (
          <div className="flex items-center gap-2" data-testid="dashboard-date-filter">
            <Filter className="h-4 w-4 text-wt-text-muted" />
            <Select value={dateFilter} onValueChange={(v) => {
              setDateFilter(v);
              if (v === 'custom') {
                setCustomCalendarOpen(true);
              }
            }}>
              <SelectTrigger className="w-[180px] bg-wt-subtle border-wt-border text-wt-text-primary">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-wt-paper border-wt-border">
                <SelectItem value="today" className="text-wt-text-primary">Today</SelectItem>
                <SelectItem value="yesterday" className="text-wt-text-primary">Yesterday</SelectItem>
                <SelectItem value="7" className="text-wt-text-primary">Last 7 days</SelectItem>
                <SelectItem value="15" className="text-wt-text-primary">Last 15 days</SelectItem>
                <SelectItem value="30" className="text-wt-text-primary">Last 30 days</SelectItem>
                <SelectItem value="90" className="text-wt-text-primary">Last 90 days</SelectItem>
                <SelectItem value="365" className="text-wt-text-primary">Last year</SelectItem>
                <SelectItem value="custom" className="text-wt-text-primary">Custom range…</SelectItem>
              </SelectContent>
            </Select>

            {dateFilter === 'custom' && (
              <Popover open={customCalendarOpen} onOpenChange={setCustomCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="bg-wt-subtle border-wt-border text-wt-text-primary" data-testid="custom-date-btn">
                    <Calendar className="h-3.5 w-3.5 mr-1.5" />
                    {customRange.from && customRange.to
                      ? `${new Date(customRange.from).toLocaleDateString()} – ${new Date(customRange.to).toLocaleDateString()}`
                      : 'Pick dates'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-wt-paper border-wt-border" align="end">
                  <CalendarPicker
                    mode="range"
                    selected={{ from: customRange.from, to: customRange.to }}
                    onSelect={(range) => {
                      setCustomRange({ from: range?.from || null, to: range?.to || null });
                      if (range?.from && range?.to) {
                        setCustomCalendarOpen(false);
                      }
                    }}
                    numberOfMonths={2}
                  />
                </PopoverContent>
              </Popover>
            )}

            {/* Compare-to-previous-period toggle. When enabled, backend
                returns `deltas` dict and each KPI card shows a ↗/↘ chip. */}
            <div className="flex items-center gap-2 pl-2 border-l border-wt-border ml-1" data-testid="compare-toggle-wrapper">
              <Switch
                id="compare-mode"
                checked={compareMode}
                onCheckedChange={setCompareMode}
                data-testid="compare-toggle"
              />
              <Label htmlFor="compare-mode" className="text-xs text-wt-text-muted cursor-pointer whitespace-nowrap">
                Compare vs previous
              </Label>
            </div>
          </div>
        )}
      </div>

      {/* Announcements Banner */}
      {announcements.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-wt-gold/10 to-transparent border border-wt-gold/20 rounded-xl p-4"
        >
          <div className="flex items-start gap-3">
            <Megaphone className="h-5 w-5 text-wt-gold flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-wt-text-primary font-medium">{announcements[0].title}</h4>
              <p className="text-sm text-wt-text-secondary line-clamp-1">{announcements[0].message}</p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => navigate('/announcements')} className="text-wt-gold">
              View All
            </Button>
          </div>
        </motion.div>
      )}

      {/* Admin/Analytics Dashboard */}
      {canViewAnalytics && stats && (
        <>
          {/* Revenue Pipeline Section */}
          <div className="space-y-4">
            <h2 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-wt-gold" />
              Revenue Overview
            </h2>
            
            {/* Leads Revenue Row — hidden if tenant has no leads yet */}
            {(stats.activity_flags?.has_leads ?? (stats.total_leads > 0 || stats.estimated_revenue > 0 || stats.won_revenue > 0)) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="revenue-leads-row">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-yellow-500/10">
                    <Target className="h-5 w-5 text-yellow-400" />
                  </div>
                  <span className="text-sm text-wt-text-muted">Estimated (Open Leads)</span>
                </div>
                <p className="text-2xl font-bold text-yellow-400">{formatCurrency(stats.estimated_revenue)}</p>
                <p className="text-xs text-wt-text-muted mt-1">{stats.open_leads} open leads</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.05 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <TrendingUp className="h-5 w-5 text-green-400" />
                  </div>
                  <span className="text-sm text-wt-text-muted">Won (Closed Leads)</span>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(stats.won_revenue)}</p>
                {stats.deltas && (
                  <div className="mt-1">
                    <DeltaChip value={stats.deltas.won_revenue} testid="delta-won-revenue" />
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-red-500/10">
                    <TrendingDown className="h-5 w-5 text-red-400" />
                  </div>
                  <span className="text-sm text-wt-text-muted">Lost (Lost Leads)</span>
                </div>
                <p className="text-2xl font-bold text-red-400">{formatCurrency(stats.lost_revenue)}</p>
              </motion.div>
            </div>
            )}

            {/* Orders Revenue Row — hidden if tenant has no orders yet */}
            {(stats.activity_flags?.has_orders ?? (stats.total_orders > 0)) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="revenue-orders-row">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-wt-gold/10">
                    <ShoppingCart className="h-5 w-5 text-wt-gold" />
                  </div>
                  <span className="text-sm text-wt-text-muted">Total Orders Revenue</span>
                </div>
                <p className="text-2xl font-bold text-wt-gold">{formatCurrency(stats.total_orders_revenue)}</p>
                <p className="text-xs text-wt-text-muted mt-1">{stats.total_orders} orders</p>
                {stats.deltas && (
                  <div className="mt-2">
                    <DeltaChip value={stats.deltas.total_orders_revenue} testid="delta-orders-revenue" />
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-orange-500/10">
                    <Clock className="h-5 w-5 text-orange-400" />
                  </div>
                  <span className="text-sm text-wt-text-muted">Unfulfilled Orders</span>
                </div>
                <p className="text-2xl font-bold text-orange-400">{formatCurrency(stats.unfulfilled_orders_revenue)}</p>
                <p className="text-xs text-wt-text-muted mt-1">{stats.unfulfilled_count} pending</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg bg-green-500/10">
                    <CheckCircle className="h-5 w-5 text-green-400" />
                  </div>
                  <span className="text-sm text-wt-text-muted">Delivered Orders</span>
                </div>
                <p className="text-2xl font-bold text-green-400">{formatCurrency(stats.delivered_orders_revenue)}</p>
                <p className="text-xs text-wt-text-muted mt-1">{stats.fulfilled_count} delivered</p>
                {stats.deltas && (
                  <div className="mt-2">
                    <DeltaChip value={stats.deltas.delivered_orders_revenue} testid="delta-delivered-revenue" />
                  </div>
                )}
              </motion.div>
            </div>
            )}

            {/* === NEW: Revenue by Channel — horizontal breakdown bars === */}
            {stats.activity_flags?.has_channel_revenue && stats.revenue_by_channel?.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.28 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
                data-testid="revenue-by-channel"
              >
                <div className="flex items-center gap-2 mb-4">
                  <Store className="h-5 w-5 text-wt-gold" />
                  <h3 className="font-semibold text-wt-text-primary">Revenue by Sales Channel</h3>
                </div>
                <div className="space-y-3">
                  {(() => {
                    const total = stats.revenue_by_channel.reduce((s, c) => s + (c.revenue || 0), 0) || 1;
                    const channelColors = {
                      shopify: 'bg-green-500',
                      amazon: 'bg-orange-500',
                      flipkart: 'bg-blue-500',
                      meesho: 'bg-pink-500',
                      woocommerce: 'bg-purple-500',
                      custom: 'bg-wt-gold',
                      lead: 'bg-cyan-500',
                      direct: 'bg-wt-gold',
                    };
                    const channelLabels = {
                      shopify: 'Shopify',
                      amazon: 'Amazon',
                      flipkart: 'Flipkart',
                      meesho: 'Meesho',
                      woocommerce: 'WooCommerce',
                      custom: 'Manual / Custom',
                      lead: 'Lead Conversion',
                      direct: 'Direct',
                    };
                    return stats.revenue_by_channel.map((c) => {
                      const pct = Math.round(((c.revenue || 0) / total) * 100);
                      // `c.channel` is now the merchant's display_name (e.g.
                      // "Shopify Furniture") with a fallback to the channel
                      // type slug. `c.channel_type` is the normalised slug
                      // we drive colour off so each store gets the brand
                      // colour for its parent channel.
                      const slug = (c.channel_type || c.channel || '').toLowerCase();
                      const label = channelLabels[slug] || c.channel;
                      const color = channelColors[slug] || 'bg-wt-gold';
                      return (
                        <div key={c.channel} className="space-y-1">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-wt-text-primary">{label}</span>
                            <span className="text-wt-text-muted">{formatCurrency(c.revenue || 0)} · {c.orders} orders · {pct}%</span>
                          </div>
                          <div className="h-2 bg-wt-subtle rounded-full overflow-hidden">
                            <div className={`h-full ${color} transition-all`} style={{ width: `${pct}%` }} />
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              </motion.div>
            )}

            {/* === NEW: Marketing-attributed revenue (Journeys + Broadcasts) === */}
            {(stats.activity_flags?.has_journey_revenue || stats.activity_flags?.has_broadcast_revenue) && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="marketing-revenue-row">
                {stats.activity_flags?.has_journey_revenue && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="bg-wt-paper border border-wt-border rounded-xl p-5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-purple-500/10">
                        <Zap className="h-5 w-5 text-purple-400" />
                      </div>
                      <div>
                        <p className="text-sm text-wt-text-muted">Journey-Attributed Revenue</p>
                        <p className="text-xl font-bold text-purple-400">{formatCurrency(stats.journey_attributed_revenue || 0)}</p>
                        {stats.deltas && (
                          <div className="mt-1">
                            <DeltaChip value={stats.deltas.journey_attributed_revenue} testid="delta-journey-revenue" />
                          </div>
                        )}
                      </div>
                    </div>
                    {stats.journey_revenue_breakdown?.length > 0 && (
                      <div className="space-y-2 mt-3 pt-3 border-t border-wt-border">
                        <p className="text-xs text-wt-text-muted uppercase tracking-wide">Top Journeys</p>
                        {stats.journey_revenue_breakdown.slice(0, 5).map((j) => (
                          <div key={j.journey_id} className="flex items-center justify-between text-sm">
                            <span className="text-wt-text-primary truncate max-w-[60%]" title={j.name}>{j.name}</span>
                            <span className="text-wt-text-muted">{formatCurrency(j.revenue)} · {j.orders}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </motion.div>
                )}

                {stats.activity_flags?.has_broadcast_revenue && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.32 }}
                    className="bg-wt-paper border border-wt-border rounded-xl p-5"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-blue-500/10">
                        <Megaphone className="h-5 w-5 text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-wt-text-muted">Broadcast-Attributed Revenue</p>
                        <p className="text-xl font-bold text-blue-400">{formatCurrency(stats.broadcast_attributed_revenue || 0)}</p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            )}
          </div>

          {/* Deliveries Section — hidden when tenant has no orders */}
          {(stats.activity_flags?.has_orders ?? (stats.total_orders > 0)) && (
          <div className="space-y-4" data-testid="deliveries-overview-section">
            <h2 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
              <Truck className="h-5 w-5 text-wt-gold" />
              Deliveries Overview
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-orange-400" />
                  <span className="text-xs text-wt-text-muted">Unfulfilled</span>
                </div>
                <p className="text-3xl font-bold text-orange-400">{stats.unfulfilled_count}</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.35 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="h-4 w-4 text-green-400" />
                  <span className="text-xs text-wt-text-muted">Fulfilled</span>
                </div>
                <p className="text-3xl font-bold text-green-400">{stats.fulfilled_count}</p>
                {stats.deltas && (
                  <div className="mt-1">
                    <DeltaChip value={stats.deltas.fulfilled_count} testid="delta-fulfilled-count" />
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="h-4 w-4 text-red-400" />
                  <span className="text-xs text-wt-text-muted">Failed</span>
                </div>
                <p className="text-3xl font-bold text-red-400">{stats.failed_count}</p>
                {stats.deltas && (
                  <div className="mt-1">
                    <DeltaChip value={stats.deltas.failed_count} invert testid="delta-failed-count" />
                  </div>
                )}
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45 }}
                className="bg-wt-paper border border-wt-border rounded-xl p-5"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Timer className="h-4 w-4 text-blue-400" />
                  <span className="text-xs text-wt-text-muted">Avg. Time</span>
                </div>
                <p className="text-3xl font-bold text-blue-400">{stats.avg_delivery_time}<span className="text-lg">m</span></p>
              </motion.div>
            </div>
          </div>
          )}

          {/* Best Performing Driver - Month Wise — hidden when tenant has no drivers */}
          {modulesEnabled.drivers && driverPerformance && (stats.activity_flags?.has_drivers ?? true) && (driverPerformance.best_driver || (driverPerformance.drivers?.length > 0)) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-wt-text-primary flex items-center gap-2">
                  <Award className="h-5 w-5 text-wt-gold" />
                  Driver Performance
                </h2>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="ghost" onClick={() => changeMonth('prev')} className="h-8 w-8 p-0">
                    <ChevronLeft className="h-4 w-4 text-wt-text-muted" />
                  </Button>
                  <span className="text-sm text-wt-text-primary min-w-[100px] text-center">
                    {monthNames[selectedMonth - 1]} {selectedYear}
                  </span>
                  <Button size="sm" variant="ghost" onClick={() => changeMonth('next')} className="h-8 w-8 p-0">
                    <ChevronRight className="h-4 w-4 text-wt-text-muted" />
                  </Button>
                </div>
              </div>

              {driverPerformance.best_driver && (
                <div className="bg-gradient-to-r from-wt-gold/10 to-transparent border border-wt-gold/20 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-full bg-wt-gold/20">
                      <Award className="h-6 w-6 text-wt-gold" />
                    </div>
                    <div>
                      <p className="text-sm text-wt-text-muted">Best Performer</p>
                      <p className="text-lg font-semibold text-wt-gold">{driverPerformance.best_driver.driver_name}</p>
                    </div>
                    <div className="ml-auto text-right">
                      <p className="text-2xl font-bold text-wt-text-primary">{driverPerformance.best_driver.deliveries}</p>
                      <p className="text-xs text-wt-text-muted">deliveries</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                {driverPerformance.drivers.slice(0, 5).map((driver, idx) => (
                  <div key={driver.driver_id} className="flex items-center gap-3 p-3 bg-wt-subtle rounded-lg">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                      idx === 0 ? 'bg-wt-gold text-black' : 'bg-wt-border text-wt-text-muted'
                    }`}>
                      {idx + 1}
                    </span>
                    <span className="flex-1 text-wt-text-primary">{driver.driver_name}</span>
                    <div className="flex items-center gap-4 text-sm">
                      <span className="text-green-400">{driver.deliveries} delivered</span>
                      <span className="text-red-400">{driver.failed} failed</span>
                      <span className="text-blue-400">{driver.avg_delivery_time}m avg</span>
                      <span className="text-wt-text-muted">{driver.success_rate}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Alerts Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {stats.low_stock_items > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-5"
              >
                <div className="flex items-center gap-3">
                  <AlertCircle className="h-6 w-6 text-orange-400" />
                  <div>
                    <h3 className="font-medium text-orange-400">Low Stock Alert</h3>
                    <p className="text-sm text-wt-text-secondary">{stats.low_stock_items} items running low</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => navigate('/inventory')} className="ml-auto text-orange-400">
                    View
                  </Button>
                </div>
              </motion.div>
            )}

            {upcomingReminders.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-5"
              >
                <div className="flex items-center gap-3">
                  <Bell className="h-6 w-6 text-blue-400" />
                  <div>
                    <h3 className="font-medium text-blue-400">Upcoming Reminders</h3>
                    <p className="text-sm text-wt-text-secondary">{upcomingReminders.length} pending follow-ups</p>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => navigate('/leads')} className="ml-auto text-blue-400">
                    View
                  </Button>
                </div>
              </motion.div>
            )}
          </div>
        </>
      )}

      {/* Driver Dashboard — only show when tenant actually has drivers AND data */}
      {modulesEnabled.drivers && canViewDeliveries && driverStats
        && (stats?.activity_flags?.has_drivers ?? true)
        && (driverStats.active_orders > 0 || driverStats.deliveries_24h > 0 || driverStats.deliveries_7d > 0 || driverStats.deliveries_30d > 0)
        && (
        <>
          {/* Driver Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <Package className="h-4 w-4 text-orange-400" />
                <span className="text-xs text-wt-text-muted">Active</span>
              </div>
              <p className="text-3xl font-bold text-orange-400">{driverStats.active_orders}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.05 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-xs text-wt-text-muted">Today</span>
              </div>
              <p className="text-3xl font-bold text-green-400">{driverStats.deliveries_24h}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="h-4 w-4 text-blue-400" />
                <span className="text-xs text-wt-text-muted">This Week</span>
              </div>
              <p className="text-3xl font-bold text-blue-400">{driverStats.deliveries_7d}</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-5"
            >
              <div className="flex items-center gap-2 mb-2">
                <Award className="h-4 w-4 text-wt-gold" />
                <span className="text-xs text-wt-text-muted">Success Rate</span>
              </div>
              <p className="text-3xl font-bold text-wt-gold">{driverStats.success_rate}%</p>
            </motion.div>
          </div>

          {/* Driver Performance Stats */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <h3 className="font-semibold text-wt-text-primary mb-4">Performance Summary</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-wt-subtle rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{driverStats.deliveries_30d}</p>
                <p className="text-xs text-wt-text-muted">This Month</p>
              </div>
              <div className="text-center p-4 bg-wt-subtle rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{driverStats.total_delivered}</p>
                <p className="text-xs text-wt-text-muted">Total Delivered</p>
              </div>
              <div className="text-center p-4 bg-wt-subtle rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{driverStats.total_failed}</p>
                <p className="text-xs text-wt-text-muted">Total Failed</p>
              </div>
              <div className="text-center p-4 bg-wt-subtle rounded-lg">
                <p className="text-2xl font-bold text-wt-text-primary">{driverStats.avg_delivery_time}m</p>
                <p className="text-xs text-wt-text-muted">Avg Time</p>
              </div>
            </div>
          </motion.div>

          {/* Driver Pending Cash */}
          {driverCash && driverCash.pending_cash > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25 }}
              className="bg-orange-500/10 border border-orange-500/20 rounded-xl p-6"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Wallet className="h-8 w-8 text-orange-400" />
                  <div>
                    <h3 className="font-semibold text-wt-text-primary">Pending Cash</h3>
                    <p className="text-sm text-wt-text-muted">{driverCash.orders?.length || 0} orders</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-2xl font-bold text-orange-400">{formatCurrency(driverCash.pending_cash)}</p>
                  <Button
                    size="sm"
                    onClick={() => navigate('/cash')}
                    className="mt-2 bg-orange-500 hover:bg-orange-600 text-white"
                  >
                    Hand Over
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* Driver Incentives */}
          {incentives && (incentives.total_incentives > 0 || incentives.total_penalties > 0) && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-6"
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-wt-text-primary flex items-center gap-2">
                  <Award className="h-5 w-5 text-wt-gold" />
                  Incentives & Penalties
                </h3>
                <Button size="sm" variant="ghost" onClick={() => navigate('/incentives')} className="text-wt-gold">
                  View All
                </Button>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <p className="text-sm text-wt-text-muted">Incentives</p>
                  <p className="text-xl font-bold text-green-400">+{formatCurrency(incentives.total_incentives)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-wt-text-muted">Penalties</p>
                  <p className="text-xl font-bold text-red-400">-{formatCurrency(incentives.total_penalties)}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-wt-text-muted">Net</p>
                  <p className={`text-xl font-bold ${incentives.net_total >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                    {incentives.net_total >= 0 ? '+' : ''}{formatCurrency(incentives.net_total)}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="bg-wt-paper border border-wt-border rounded-xl p-6"
          >
            <h3 className="font-semibold text-wt-text-primary mb-4">Quick Actions</h3>
            <div className="flex flex-wrap gap-3">
              <Button onClick={() => navigate('/my-deliveries')} className="bg-wt-gold hover:bg-wt-gold-hover text-black">
                <Package className="h-4 w-4 mr-2" />
                My Deliveries
              </Button>
              <Button onClick={() => navigate('/cash')} variant="outline" className="border-wt-border text-wt-text-primary">
                <Wallet className="h-4 w-4 mr-2" />
                Cash Management
              </Button>
              <Button onClick={() => navigate('/profile')} variant="outline" className="border-wt-border text-wt-text-primary">
                Edit Profile
              </Button>
            </div>
          </motion.div>
        </>
      )}

      {/* Non-admin users without dashboard analytics or delivery permissions */}
      {/* User-Specific Dashboard (non-admin) */}
      {!isAdmin && (
        <div className="space-y-6">
          {/* Driver Rank & Motivation Widget - for users with assigned orders permission */}
          {(canViewAssignedOrders || canViewDeliveries) && myRank && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gradient-to-r from-wt-gold/10 via-wt-paper to-wt-paper border border-wt-gold/30 rounded-xl p-6"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-wt-gold/20 flex items-center justify-center">
                    <Award className="h-8 w-8 text-wt-gold" />
                  </div>
                  <div>
                    <p className="text-sm text-wt-text-muted">{myRank.month} Rank</p>
                    <p className="text-3xl font-bold text-wt-gold">#{myRank.my_rank}</p>
                    <p className="text-sm text-wt-text-secondary">
                      out of {myRank.total_drivers} drivers • {myRank.my_deliveries} deliveries
                    </p>
                  </div>
                </div>
                
                <div className="text-center md:text-right">
                  {myRank.my_rank === 1 ? (
                    <div className="text-green-400">
                      <Trophy className="h-6 w-6 inline-block mr-2" />
                      <span className="font-semibold">You're #1!</span>
                      <p className="text-sm text-wt-text-muted mt-1">Keep up the great work!</p>
                    </div>
                  ) : myRank.next_rank_gap > 0 ? (
                    <>
                      <p className="text-wt-text-primary font-medium">
                        {myRank.next_rank_gap} more delivery{myRank.next_rank_gap > 1 ? 'ies' : ''} to reach #{myRank.my_rank - 1}
                      </p>
                      <p className="text-sm text-wt-text-muted mt-1">
                        Leader has {myRank.leader_deliveries} deliveries
                      </p>
                    </>
                  ) : (
                    <p className="text-wt-text-secondary">Complete deliveries to climb the ranks!</p>
                  )}
                </div>
              </div>
            </motion.div>
          )}

          {/* Available Rewards Badge */}
          {availableRewards.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-4 cursor-pointer hover:border-wt-gold/50 transition-colors"
              onClick={() => navigate('/incentives')}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Target className="h-5 w-5 text-green-400" />
                  </div>
                  <div>
                    <p className="text-wt-text-primary font-medium">{availableRewards.length} Rewards Available</p>
                    <p className="text-sm text-wt-text-muted">Earn incentives by achieving goals</p>
                  </div>
                </div>
                <Button variant="outline" size="sm" className="border-wt-gold text-wt-gold">
                  Check Now
                </Button>
              </div>
            </motion.div>
          )}

          {/* User Stats Based on Permissions */}
          {userStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Orders Stats - for users with order access */}
              {userStats.orders && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-wt-paper border border-wt-border rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-wt-text-primary font-semibold flex items-center gap-2">
                      <Truck className="h-5 w-5 text-wt-gold" />
                      {canViewAssignedOrders && !canViewOrders ? 'My Deliveries' : 'Orders Overview'}
                    </h3>
                    <span className="text-2xl font-bold text-wt-gold">{userStats.orders.total}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-yellow-500/10 rounded-lg p-3 text-center cursor-pointer hover:bg-yellow-500/20"
                         onClick={() => navigate('/orders?status=pending')}>
                      <p className="text-xl font-bold text-yellow-400">{userStats.orders.unfulfilled}</p>
                      <p className="text-xs text-wt-text-muted">Pending</p>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-3 text-center cursor-pointer hover:bg-blue-500/20"
                         onClick={() => navigate('/orders?status=picked')}>
                      <p className="text-xl font-bold text-blue-400">{userStats.orders.in_progress}</p>
                      <p className="text-xs text-wt-text-muted">In Progress</p>
                    </div>
                    <div className="bg-green-500/10 rounded-lg p-3 text-center cursor-pointer hover:bg-green-500/20"
                         onClick={() => navigate('/orders?status=delivered')}>
                      <p className="text-xl font-bold text-green-400">{userStats.orders.delivered}</p>
                      <p className="text-xs text-wt-text-muted">Delivered</p>
                    </div>
                    <div className="bg-red-500/10 rounded-lg p-3 text-center cursor-pointer hover:bg-red-500/20"
                         onClick={() => navigate('/orders?status=failed')}>
                      <p className="text-xl font-bold text-red-400">{userStats.orders.failed}</p>
                      <p className="text-xs text-wt-text-muted">Failed</p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Leads Stats */}
              {userStats.leads && Object.keys(userStats.leads).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-wt-paper border border-wt-border rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-wt-text-primary font-semibold flex items-center gap-2">
                      <Users className="h-5 w-5 text-wt-gold" />
                      Leads by Status
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(userStats.leads).map(([stage, count]) => (
                      <div key={stage} 
                           className="flex items-center justify-between p-2 rounded-lg hover:bg-wt-subtle cursor-pointer"
                           onClick={() => navigate(`/leads?stage=${stage}`)}>
                        <span className="text-wt-text-secondary capitalize">{stage.replace('_', ' ')}</span>
                        <span className="font-semibold text-wt-text-primary">{count}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Vendor Pickups Stats */}
              {userStats.pickups && Object.keys(userStats.pickups).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-wt-paper border border-wt-border rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-wt-text-primary font-semibold flex items-center gap-2">
                      <Package className="h-5 w-5 text-wt-gold" />
                      Vendor Pickups
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(userStats.pickups).map(([status, count]) => (
                      <div key={status} 
                           className="flex items-center justify-between p-2 rounded-lg hover:bg-wt-subtle cursor-pointer"
                           onClick={() => navigate(`/vendor-pickups?status=${status}`)}>
                        <span className="text-wt-text-secondary capitalize">{status.replace('_', ' ')}</span>
                        <span className="font-semibold text-wt-text-primary">{count}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Purchase Orders Stats */}
              {userStats.purchase_orders && Object.keys(userStats.purchase_orders).length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                  className="bg-wt-paper border border-wt-border rounded-xl p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-wt-text-primary font-semibold flex items-center gap-2">
                      <ShoppingCart className="h-5 w-5 text-wt-gold" />
                      Purchase Orders
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(userStats.purchase_orders).map(([status, count]) => (
                      <div key={status} 
                           className="flex items-center justify-between p-2 rounded-lg hover:bg-wt-subtle cursor-pointer"
                           onClick={() => navigate(`/purchase-orders?status=${status}`)}>
                        <span className="text-wt-text-secondary capitalize">{status.replace('_', ' ')}</span>
                        <span className="font-semibold text-wt-text-primary">{count}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          )}

          {/* Show welcome message if no stats available */}
          {!userStats && !myRank && availableRewards.length === 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-wt-paper border border-wt-border rounded-xl p-12 text-center"
            >
              <div className="max-w-md mx-auto">
                <h2 className="font-playfair text-3xl font-bold text-wt-text-primary mb-4">
                  Welcome to Opswarex
                </h2>
                <p className="text-wt-text-secondary mb-6">
                  Navigate using the sidebar to access your assigned modules and tasks.
                </p>
              </div>
            </motion.div>
          )}
        </div>
      )}

      {/* Empty State for Non-Admin Non-Driver Users - FALLBACK */}
      {!isAdmin && !userStats && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-wt-paper border border-wt-border rounded-xl p-12 text-center"
        >
          <div className="max-w-md mx-auto">
            <h2 className="font-playfair text-3xl font-bold text-wt-text-primary mb-4">
              Welcome to Opswarex
            </h2>
            <p className="text-wt-text-secondary mb-6">
              Navigate using the sidebar to access your assigned modules and tasks.
            </p>
          </div>
        </motion.div>
      )}
    </div>
    </PullToRefresh>
  );
};

export default DashboardPage;
